import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BillingInterval, ChangePlanRequest, PlanTier } from '@wf/shared'
import { ApiError, api, type BillingStatusResponse } from '../api/client'

// Abo & Lizenzen (nur Chef). Der Status ist die eine Wahrheit für Preis/Seats/Zustand; die Nutzerliste
// (useSeatUsers) ist rein informativ. Polling nach der Mandats-Rückkehr steuert die Seite über `pollMs`.

export const BILLING_STATUS_KEY = ['billing', 'status'] as const

/** `GET /billing/status`. `pollMs` aktiviert das Nach-Checkout-Polling (sonst kein Intervall). */
export function useBillingStatus(pollMs: number | false = false) {
  return useQuery<BillingStatusResponse>({
    queryKey: BILLING_STATUS_KEY,
    queryFn: api.billingStatus,
    refetchInterval: pollMs,
  })
}

/** Aktive Nutzer als „belegte Seats" (rein informativ) — die abrechnungsrelevante Zahl kommt aus dem Status. */
export function useSeatUsers() {
  return useQuery({ queryKey: ['users'], queryFn: api.listUsers })
}

/** Monatliche Abo-Rechnungen (Cevio → Werkstatt) für die Liste auf „Abo & Lizenzen". */
export function useAboInvoices() {
  return useQuery({ queryKey: ['billing', 'invoices'], queryFn: api.aboInvoices })
}

/** Einzelner Abo-Rechnungsbeleg (Druck/PDF-Seite). */
export function useAboInvoice(id: number) {
  return useQuery({ queryKey: ['billing', 'invoice', id], queryFn: () => api.aboInvoice(id), enabled: Number.isFinite(id) })
}

/**
 * Reconcile-Polling nach dem Checkout-Rücksprung: holt aktiv den echten Mandatsstatus vom Anbieter (statt
 * nur passiv auf den Webhook zu warten) und spiegelt das Ergebnis in den Status-Cache. Nur aktiv, solange
 * `active` — sonst kein Anbieter-Aufruf (kein Polling im Normalbetrieb).
 */
export function useBillingReconcilePolling(active: boolean) {
  const qc = useQueryClient()
  return useQuery<BillingStatusResponse>({
    queryKey: ['billing', 'reconcile'],
    queryFn: async () => {
      const fresh = await api.billingReconcile()
      qc.setQueryData(BILLING_STATUS_KEY, fresh)
      return fresh
    },
    enabled: active,
    refetchInterval: active ? 3000 : false,
    gcTime: 0,
  })
}

/** Manueller „Status aktualisieren"-Knopf: einmal reconcilen (behebt verpasste Webhooks / verhakte Stände). */
export function useBillingReconcileOnce() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.billingReconcile(),
    onSuccess: (data) => qc.setQueryData(BILLING_STATUS_KEY, data),
  })
}

/** SEPA-Mandat einrichten → Redirect-URL. Optional Tarif + Intervall mitwählen. Das Weiterleiten übernimmt die Seite. */
export function useBillingCheckout() {
  return useMutation({
    mutationFn: (choice: { planTier?: PlanTier; billingInterval?: BillingInterval } = {}) => api.billingCheckout(choice),
  })
}

/** Tarif wechseln (CAS über `version`). Erfolg schreibt den neuen Status in den Cache; 409 → frisch laden. */
export function useBillingChangePlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChangePlanRequest) => api.billingChangePlan(body),
    onSuccess: (data) => qc.setQueryData(BILLING_STATUS_KEY, data),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) void qc.invalidateQueries({ queryKey: BILLING_STATUS_KEY })
    },
  })
}

/** Abo kündigen (CAS über `version`). Erfolg schreibt den neuen Status direkt in den Cache. */
export function useBillingCancel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (version: number) => api.billingCancel(version),
    onSuccess: (data) => qc.setQueryData(BILLING_STATUS_KEY, data),
    // 409 = Version veraltet: frischen Stand ziehen (nie blind mit alter Version erneut senden).
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) void qc.invalidateQueries({ queryKey: BILLING_STATUS_KEY })
    },
  })
}
